import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import { history, Prompt, Location } from 'umi';
import { Modal, ConfirmOptions } from 'tea-component';
import './index.scss';

/**
 * RoutePrompt Component - Strategic Version
 * 
 * KEY DIFFERENCES from globalpedia RoutePrompt:
 * 
 * 1. MULTIPLE INSTANCES SUPPORT:
 *    - Uses a registry system (activeOnOkCallbacks) to collect callbacks from multiple RoutePrompt instances
 *    - When navigation is triggered, ALL active callbacks are executed in parallel
 *    - This allows multiple tables/components (e.g., GRBP + Decision Points) to save together
 *    - Globalpedia version only handles a single onOk callback
 * 
 * 2. NAVIGATION APPROACH:
 *    - Uses history.push(location) DIRECTLY in onOk callback (no state/useEffect)
 *    - WHY: When when becomes false after save (e.g., PlainText calls setIsEditing(false)),
 *           the component unmounts. Using setLocation() → useEffect would trigger React warnings
 *           about state updates on unmounted components. Direct navigation avoids this.
 *    - Globalpedia uses setLocation() → useEffect pattern (works for them because their when
 *      condition is simpler and doesn't change immediately after save)
 * 
 * 3. DUPLICATE MODAL PREVENTION:
 *    - Uses isPromptModalShowing flag to prevent multiple modals from showing simultaneously
 *    - Globalpedia doesn't have this protection
 * 
 * 4. WHEN CONDITION:
 *    - Uses just when (simplified)
 *    - Globalpedia uses when && !location (to prevent re-triggering while navigating)
 *    - We don't need !location because we navigate directly, not via state
 * 
 * 5. ERROR HANDLING:
 *    - Wraps callback execution in try-catch
 *    - Still proceeds with navigation even if save fails
 *    - Globalpedia doesn't have explicit error handling
 * 
 * 6. SAFETY FEATURES:
 *    - 10-second timeout to reset isPromptModalShowing flag (prevents stuck state)
 *    - Globalpedia doesn't have this
 */

interface IRoutePromptProps extends Omit<ConfirmOptions, 'message' | 'description'> {
  // 是否进行路由拦截
  when: boolean;
  // Optional message and description with defaults
  message?: ConfirmOptions['message'];
  description?: ConfirmOptions['description'];
}

// Global flag to prevent multiple RoutePrompt modals from showing simultaneously
let isPromptModalShowing = false;

// Registry to collect all active RoutePrompt onOk callbacks when navigation is triggered
// This allows multiple RoutePrompt instances (e.g., GRBP + Decision Points) to execute
// their save operations together when user confirms navigation
type OnOkCallback = (() => void | Promise<void>) | undefined;
const activeOnOkCallbacks = new Set<OnOkCallback>();

const RoutePrompt: React.FC<IRoutePromptProps> = (props) => {
  const { 
    style, 
    className, 
    when, 
    okText = 'Save', 
    cancelText = 'Cancel', 
    message = 'Save Changes?',
    description: descriptionProp,
    onOk, 
    onCancel, 
    ...restProps 
  } = props;

  // Default description split into two lines
  const description = descriptionProp ?? (
    <div>
      <div>You have unsaved changes.</div>
      <div>Do you want to save them before leaving this page?</div>
    </div>
  );

  const modalRef = useRef<any>(null);

  // Register/unregister this instance's onOk callback based on when prop
  useEffect(() => {
    if (when && onOk) {
      activeOnOkCallbacks.add(onOk);
    } else {
      activeOnOkCallbacks.delete(onOk);
    }
    
    return () => {
      // Cleanup: remove this callback when component unmounts
      activeOnOkCallbacks.delete(onOk);
    };
  }, [when, onOk]);

  // Note: Navigation is now handled directly in onOk callback using history.push()
  // This avoids issues when the component unmounts after when becomes false

  // Cleanup: Reset flag when component unmounts or when changes
  useEffect(() => {
    return () => {
      // Reset flag when component unmounts
      if (modalRef.current) {
        isPromptModalShowing = false;
      }
    };
  }, []);

  return (
    <Prompt
      // when为true时，表示路由切换时要进行拦截，并进行二次确认提示交互
      when={when}
      message={(location) => {
        /**
         * 让当前获取焦点的元素 丢失焦点
         * 在二次确认弹窗打开时，浏览器会把焦点从当前激活元素转移到弹窗中，会触发当前元素的onBlur事件，并且在关闭弹窗后，浏览器又会把焦点从弹窗转移回之前那个元素，从而会再次触发那个元素的onFocus事件
         * 在上面的场景下，在特殊的业务代码下，可能会造成无限循环，关闭弹窗后，立马又弹窗的问题
         * 所以在二次确认弹窗唤起后，需要让当前激活的元素失去焦点
         */
        // Prevent duplicate modals - if one is already showing, skip this one
        if (isPromptModalShowing) {
          return false;
        }

        (document.activeElement as HTMLElement)?.blur();
        isPromptModalShowing = true;
        
        modalRef.current = Modal.confirm({
          ...restProps,
          className: classnames('dbs-common-modal', 'dbs-route-prompt-modal', className),
          message,
          description,
          okText,
          cancelText,
          onOk: async () => {
            // Collect all active onOk callbacks at the time of confirmation
            // This ensures all RoutePrompt instances with unsaved changes execute their save operations
            const callbacksToExecute = Array.from(activeOnOkCallbacks).filter(Boolean) as Array<() => void | Promise<void>>;
            
            // Execute all active onOk callbacks (for all RoutePrompt instances with unsaved changes)
            try {
              const promises = callbacksToExecute.map(callback => {
                const result = callback();
                return result instanceof Promise ? result : Promise.resolve();
              });
              
              // Wait for all save operations to complete
              await Promise.all(promises);
            } catch (error) {
              console.error('Error executing RoutePrompt onOk callbacks:', error);
              // Still proceed with navigation even if one fails
            }
            
            // Clear the registry after execution
            activeOnOkCallbacks.clear();
            // Clear the flag after save completes
            isPromptModalShowing = false;
            
            // Navigate directly using history.push() - the location is available from the closure
            // WHY: When when becomes false after save (e.g., PlainText calls setIsEditing(false)),
            //      the RoutePrompt component unmounts. If we use setLocation() to trigger navigation
            //      via useEffect, React will warn about state updates on unmounted components.
            //      By navigating directly here, we avoid the unmounted component issue entirely.
            history.push(location);
          },
          onCancel: () => {
            // Don't clear the registry on cancel - it's managed by useEffect based on when prop
            // The callbacks should remain in the registry so they can be executed on the next modal
            // Clear the flag on cancel
            isPromptModalShowing = false;
            onCancel?.();
          },
        });
        
        // Safety timeout: Reset flag after 10 seconds in case modal is closed unexpectedly
        // This prevents the flag from getting stuck if something goes wrong
        setTimeout(() => {
          isPromptModalShowing = false;
        }, 10000);
        
        return false;
      }}
    />
  );
};

export default RoutePrompt;
